import { useState, useEffect, useMemo } from 'react';

export const sortAlphanumerically = (a, b, orderBy = null) => {
  if (!a) {
    return -1;
  }
  if (!b) {
    return 1;
  }

  const getValue = (val) => {
    if (Array.isArray(val)) {
      return val.join(', ');
    }
    return val;
  };

  let valA = getValue(a);
  let valB = getValue(b);

  if (!valA) {
    return -1;
  }

  if (!valB) {
    return 1;
  }

  if (!orderBy) {
    // Use Intl.Collator for natural sorting
    const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
    return collator.compare(valA, valB);
  }

  valA = getValue(a[orderBy]);
  valB = getValue(b[orderBy]);

  if (!valA) {
    return -1;
  }

  if (!valB) {
    return 1;
  }

  // Use Intl.Collator for natural sorting
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  return collator.compare(String(valA), String(valB));
};

export const sortAmounts = (a, b, orderBy = null) => {
  const parseAmount = (value) => {
    if (typeof value !== 'string') { return 0; }
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
  };

  if (!a) {
    return -1;
  }
  if (!b) {
    return 1;
  }
  if (!orderBy) {
    return parseAmount(a) - parseAmount(b);
  }
  if (a[orderBy] && b[orderBy]) {
    return parseAmount(a[orderBy]) - parseAmount(b[orderBy]);
  }
  return a[orderBy] > b[orderBy] ? 1 : -1;
};

export const sortByDate = (a, b, orderBy) => {
  if (!a) {
    return -1;
  }
  if (!b) {
    return 1;
  }
  if (!a[orderBy] && b[orderBy]) {
    return -1;
  }
  if (!b[orderBy] && a[orderBy]) {
    return 1;
  }
  // if both dates are nullish, sort by a different key instead
  // this enforces some consistent order in nullish values
  if (!b[orderBy] && !a[orderBy]) {
    const firstKey = Object.keys(a)[0];
    return sortAlphanumerically(a, b, firstKey);
  }
  return new Date(a[orderBy]) - new Date(b[orderBy]);
};

// sort by date only and not time, nullish values should be at the start
export const sortByDateOnly = (a, b, orderBy) => {
  if (!a) {
    return 1;
  }
  if (!b) {
    return -1;
  }
  if (!a[orderBy] && b[orderBy]) {
    return 1;
  }
  if (!b[orderBy] && a[orderBy]) {
    return -1;
  }
  // if both dates are nullish, sort by a different key instead
  // this enforces some consistent order in nullish values
  if (!b[orderBy] && !a[orderBy]) {
    const firstKey = Object.keys(a)[0];
    return sortAlphanumerically(a, b, firstKey);
  }

  // Convert dates to YYYY-MM-DD format for comparison
  const dateA = new Date(a[orderBy]);
  const dateB = new Date(b[orderBy]);

  const dateOnlyA = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
  const dateOnlyB = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate());

  return dateOnlyA - dateOnlyB;
};

export const getComparator = (
  order,
  orderBy,
  comparator,
  secondaryOrderBy = null,
  secondaryOrder = 'asc',
  secondaryComparator = null,
) => {
  const primaryComparator = (a, b) => comparator(a, b, orderBy);

  if (!secondaryOrderBy) {
    return order === 'asc'
      ? primaryComparator
      : (a, b) => -primaryComparator(a, b);
  }

  return order === 'asc'
    ? (a, b) => {
      const result = primaryComparator(a, b);
      if (result !== 0) { return result; }
      const secondaryResult = secondaryComparator(a, b, secondaryOrderBy);
      return secondaryOrder === 'asc' ? secondaryResult : -secondaryResult;
    } : (a, b) => {
      const result = primaryComparator(a, b);
      if (result !== 0) { return -result; }
      const secondaryResult = secondaryComparator(a, b, secondaryOrderBy);
      return secondaryOrder === 'asc' ? secondaryResult : -secondaryResult;
    };
};

/**
 * Custom hook to handle sorting an array of objects by a key and a
 * given direction, also supports setting what comparator function to use
 *
 * @param {Array} data - array of objects to sort
 * @param {string} defaultSortBy - default key and direction to sort by e.g. datePaid-asc
 * @param {Function} defaultComparator - default comparator function to use
 * @param {Function} onSortChange - callback to handle when data changes from sort
 * @param {string} [secondarySortBy] - optional secondary field to sort by when primary fields are equal
 * @param {string} [secondarySortOrder] - optional sort order for secondary field ('asc' or 'desc')
 * @param {Function} [secondaryComparator] - optional comparator function to use for secondary field
 */
export function useSort(
  data,
  defaultSortBy,
  defaultComparator,
  onSortChange,
  secondarySortBy = null,
  secondarySortOrder = 'asc',
  secondaryComparator = null,
) {
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [comparator, setComparator] = useState(() => defaultComparator);

  // Create a copy before sorting, as the original array is frozen in strict mode.
  // `useMemo` prevents multiple re-renderings if the shallow-compared value
  // of `data` hasn't changed.
  const sortedData = useMemo(() => [...data], [data]);

  useEffect(() => {
    if (sortedData.length) {
      const [orderBy, order] = sortBy.split('-');

      sortedData.sort(getComparator(
        order,
        orderBy,
        comparator,
        secondarySortBy,
        secondarySortOrder,
        secondaryComparator,
      ));

      // Compare data before calling sort change handler.
      // this could probably be optimized if we have performance issues
      // with larger data sets or complex objects
      const dataFlat = JSON.stringify(data);
      const sortedFlat = JSON.stringify(sortedData);
      if (dataFlat !== sortedFlat && onSortChange) {
        onSortChange(sortedData, sortBy);
      }
    }
  }, [
    data,
    sortedData,
    sortBy,
    comparator,
    onSortChange,
    secondarySortBy,
    secondarySortOrder,
    secondaryComparator,
  ]);

  return {
    sortBy,
    setSortBy,
    setComparator,
  };
}

export default useSort;
